// V2-4c: the "when did you finish it?" picker shared by every `watch.log`
// caller on the site — today that is only the Add panel's own search
// results (`AddPanel.jsx`), but the logic is kept separate from that
// component's own rendering so a future caller (the design's own heading
// for this feature names "the title page" too; see `docs/reports/V2-4c.md`
// for why this slice did not find a title-page control to attach it to)
// gets the identical rule rather than a second, hand-typed one.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 PRECISION IS month / year / none — `day` IS NOT OFFERED (brief §2)
// ═══════════════════════════════════════════════════════════════════════════
//
// The bot's own `watched_when` shape (`cogs/watch/requests.py::
// _validate_watched_when_payload`, mirrored in `site/functions/api/watch/
// _requestBody.js::watchedWhenPayload`) accepts `day` too, but neither the
// design's prototype (`docs/design-briefs/watch/v2/D2D Watch v2.dc.html`'s
// own `logModes`) nor this brief offers it from the site — a member typing
// "when did you finish this" from memory does not usually know the exact
// day, and the design deliberately narrows to the two precisions people
// actually have.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THE YEAR LIST IS DERIVED FROM `Date.now()`, NEVER A LITERAL RANGE
// ═══════════════════════════════════════════════════════════════════════════
//
// The prototype's own `<select>` hardcodes `2026, 2025, …, 2015` — exactly
// the kind of fixed-to-today literal CLAUDE.md gotcha 23 (`watchArchive.js`'s
// own `yearOptions` already refuses to copy) catalogues at length: correct
// on the day it was typed, silently wrong on every day after. The CEILING
// here is the current year, read live; the FLOOR (1900) is not date-relative
// at all, so it never rots — it only needs to predate anything in the
// catalog, which it comfortably does (`Twin Peaks`, 1990, is the oldest
// title in the design's own sample data).

export const LOG_MONTH = 'month'
export const LOG_YEAR = 'year'
export const LOG_NONE = 'none'

const EARLIEST_YEAR = 1900

const MONTH_NAMES = Object.freeze([
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
])

/** `{value: '01', label: 'January'}` … `{value: '12', label: 'December'}' —
 * the SELECT's own options. `value` is zero-padded to match the wire's
 * `"YYYY-MM"` shape (`_requestBody.js::MONTH_RE`); the prototype's own mock
 * used bare month abbreviations (`value="Jan"`) as a DISPLAY convenience
 * that was never meant to be sent over the wire — see the report. */
export const MONTH_OPTIONS = Object.freeze(
  MONTH_NAMES.map((label, index) => ({ value: String(index + 1).padStart(2, '0'), label })),
)

/** Every year from now back to 1900, newest first — never a fixed list.
 * `nowMs` defaults to the real clock; tests pass an explicit one so the
 * list is exercised without depending on the day the suite happens to run
 * (gotcha 23's own rule, applied to a test rather than to prose). */
export function logYearOptions(nowMs = Date.now()) {
  const currentYear = new Date(nowMs).getUTCFullYear()
  const years = []
  for (let year = currentYear; year >= EARLIEST_YEAR; year -= 1) years.push(year)
  return years
}

/** The three choices themselves, `A month` / `A year` / `Don't remember` —
 * the design's own copy, verbatim, apostrophe U+2019. */
export const LOG_PRECISION_LABELS = Object.freeze([
  [LOG_MONTH, 'A month'],
  [LOG_YEAR, 'A year'],
  [LOG_NONE, 'Don’t remember'],
])

/** `"March 2024"`, `"2024"`, or `null` for `none` — never invents a date
 * `none` deliberately withholds. `month` is the zero-padded `"01"`.."12"`
 * wire value, not an index — this function is the ONE place that maps it
 * back to a name, so `MONTH_OPTIONS` and this can never disagree. */
export function logWhenLabel(precision, month, year) {
  if (precision === LOG_YEAR) return String(year)
  if (precision === LOG_MONTH) {
    const name = MONTH_NAMES[Number(month) - 1]
    return name ? `${name} ${year}` : null
  }
  return null
}

/** `"Log it · March 2024"`, `"Log it · 2024"`, `"Log it · date unknown"` —
 * the design's own three worked examples (brief §2), built from
 * `logWhenLabel` rather than re-deriving the month name a second time. */
export function logButtonLabel(precision, month, year) {
  const label = logWhenLabel(precision, month, year)
  return label ? `Log it · ${label}` : 'Log it · date unknown'
}

/**
 * The `watched_when` payload fragment itself — `{precision, value}`,
 * exactly `site/functions/api/watch/_requestBody.js::watchedWhenPayload`'s
 * own accepted shape, so nothing this builds can ever be a shape that
 * validator refuses.
 */
export function buildWatchedWhen(precision, month, year) {
  if (precision === LOG_YEAR) return { precision: LOG_YEAR, value: String(year) }
  if (precision === LOG_MONTH) return { precision: LOG_MONTH, value: `${year}-${month}` }
  return { precision: LOG_NONE, value: null }
}
